/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useEffect } from 'react';
import list from 'lib/proforma/templates/List';
import NonEmptyArray from 'lib/NonEmptyArray';
import Blank from 'lib/Blank';
import useUploadedFiles from 'lib/useUploadedFiles';
import isNil from 'lib/t/isNil';
import cx from 'classnames';
import { Fragment } from 'react';
import * as I from 'immutable';

const buttonCss = css(`
  font-size: 1.8rem;
  padding: 1.5rem 2rem;
  display: inline-block;
  margin: 0;
  background: var(--info);
  color: white !important;
  min-width: 108px;
`);

// Set _destroy flag to '1' if the value has an id attribute.
// Otherwise, simply remove it from the keyset.
function handleDestroy(items, row) {
  const ret = (items || []).map((v, i) => {
    if (i === row.index) {
      if (I.isImmutable(v)) {
        v = v.set('_destroy', '1');
      } else {
        v._destroy = '1';
      }
    }
    return v;
  });
  return ret;
}

function AddButton({ onChange, value, onClick }) {
  const accepted = ['.pdf', 'image/*'];
  const [items, isLoading, setLoading] = useUploadedFiles(value, accepted);

  useEffect(() => {
    if (Blank.is(items)) return;
    // Add a key manually, since we disabled the default add button.
    onClick();
    onChange(items);
  }, [items]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setLoading(true)}
        disabled={isLoading}
        css={buttonCss}
        className="text-left"
        data-testid="AddButton"
      >
        <i
          className={cx('fa', {
            'fa-file-upload': !isLoading,
            'fa-spinner fa-spin': isLoading,
          })}
        />{' '}
        Upload
      </button>
    </div>
  );
}

function Item({ name, removeBtn, onRemove, index }) {
  return (
    <div data-testid={`RowItem-${index}`} className="col-sm-3 col-lg-3 p-0">
      <div className="p-2 border text-black-50 text-truncate">
        <i className="fa fa-file-pdf" /> {name}
      </div>
      {removeBtn && (
        <a
          data-testid={`RemoveBtn-${index}`}
          className="btn btn-block btn-link mt-1"
          onClick={onRemove}
        >
          {removeBtn.label}
        </a>
      )}
    </div>
  );
}

export default (list.clone({
  Fieldset(locals) {
    const { disabled, label, value, onChange, add, help, rows } = locals;

    const legend = label ? (
      <label data-testid="FieldLabel" className="col-form-label">
        {label}
      </label>
    ) : null;

    return (
      <div
        disabled={disabled}
        className="form-group form-group-depth-2 form-group-documentUploader mb-3"
      >
        {legend}
        {add && (
          <AddButton
            value={value}
            onClick={() => add.click()}
            onChange={onChange}
          />
        )}
        {NonEmptyArray.is(rows) && (
          <div data-testid="RowContainer" className="mt-3">
            {rows}
          </div>
        )}
        {help && (
          <div data-testid="HelpText" className="text-muted small mt-2">
            {help}
          </div>
        )}
      </div>
    );
  },
  Row({ row, onChange }) {
    const removeBtn = row.buttons.find(v => v.type === 'remove');
    type RowItemT = { url: string, name: string, id: any, _destroy: any };
    const value: RowItemT = ((row: any).value: any);

    if (Blank.is(value) || isNil(value)) return null;
    const { url, name, id, _destroy } = value;
    if (!url) return null;

    const onRemove = e => {
      if (!isNil(id)) {
        onChange(items => handleDestroy(items, row));
      } else if (removeBtn) {
        removeBtn.click(e);
      }
    };

    const markedForDestruction = String(_destroy) === '1';

    return (
      <Fragment>
        <div
          data-testid={`RowItemMeta-${row.index}`}
          className={cx({ hide: true })}
        >
          {row.input}
        </div>
        {!markedForDestruction && (
          <Item
            name={name}
            removeBtn={removeBtn}
            onRemove={onRemove}
            index={row.index}
          />
        )}
      </Fragment>
    );
  },
}): any);
